import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import { useState } from "react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import {
  Brain, TrendingUp, CheckCircle2, XCircle, Zap, Loader2,
  BarChart3, MessageSquare, AlertTriangle, Lightbulb, RefreshCw,
} from "lucide-react";

/** Sanitiza texto vindo do LLM para exibição segura (remove tags HTML e limita tamanho). */
function sanitizeLLMText(text: unknown, maxLen = 200): string {
  if (typeof text !== "string") return "";
  return text.replace(/<[^>]*>/g, "").slice(0, maxLen);
}

export default function AIInsights() {
  const [topicsResult, setTopicsResult] = useState<any>(null);
  const [sentimentResult, setSentimentResult] = useState<any>(null);

  const { data: metrics, isLoading: loadingMetrics } = trpc.aiInsights.metrics.useQuery({});
  const { data: daily, isLoading: loadingDaily } = trpc.aiInsights.dailyExecutions.useQuery({});

  const topicsMut = trpc.aiInsights.analyzeTopics.useMutation({
    onSuccess: (data) => setTopicsResult(data),
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const sentimentMut = trpc.aiInsights.sentimentAlert.useMutation({
    onSuccess: (data) => setSentimentResult(data),
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const maxDaily = daily ? Math.max(...daily.map(d => d.total), 1) : 1;

  return (
    <PermissionGuard permission="whatsapp:ai_insights">
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Brain className="w-6 h-6 text-indigo-600" />
              Insights de IA
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Metricas de desempenho, topicos frequentes e alertas de sentimento
            </p>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium">
                <Zap className="w-3.5 h-3.5" />
                Execucoes de fluxo
              </div>
              <div className="text-2xl font-bold mt-1">
                {loadingMetrics ? "..." : metrics?.totalExecutions ?? 0}
              </div>
              <p className="text-[11px] text-muted-foreground">ultimos 30 dias</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                Taxa de sucesso
              </div>
              <div className="text-2xl font-bold mt-1 text-emerald-600">
                {loadingMetrics ? "..." : `${metrics?.successRate ?? 0}%`}
              </div>
              <p className="text-[11px] text-muted-foreground">fluxos concluidos com sucesso</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium">
                <XCircle className="w-3.5 h-3.5 text-red-500" />
                Falhas
              </div>
              <div className="text-2xl font-bold mt-1 text-red-600">
                {loadingMetrics ? "..." : metrics?.failedExecutions ?? 0}
              </div>
              <p className="text-[11px] text-muted-foreground">execucoes com erro</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium">
                <BarChart3 className="w-3.5 h-3.5 text-indigo-500" />
                Fluxos com IA
              </div>
              <div className="text-2xl font-bold mt-1">
                {loadingMetrics ? "..." : metrics?.totalFlowsWithAI ?? 0}
              </div>
              <p className="text-[11px] text-muted-foreground">fluxos ativos</p>
            </CardContent>
          </Card>
        </div>

        {/* Daily chart + Sentiment side by side */}
        <div className="grid md:grid-cols-3 gap-4">
          {/* Daily executions chart */}
          <Card className="md:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-indigo-500" />
                Execucoes por dia (14 dias)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingDaily ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : !daily || daily.length === 0 ? (
                <p className="text-xs text-muted-foreground py-8 text-center">Sem dados ainda</p>
              ) : (
                <div className="flex items-end gap-1 h-32">
                  {daily.map((d, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full flex flex-col items-center">
                        <div
                          className="w-full bg-emerald-400 rounded-t"
                          style={{ height: `${Math.max(4, (d.completed / maxDaily) * 100)}px` }}
                          title={`${d.completed} sucesso`}
                        />
                        {d.failed > 0 && (
                          <div
                            className="w-full bg-red-400 rounded-b"
                            style={{ height: `${Math.max(2, (d.failed / maxDaily) * 100)}px` }}
                            title={`${d.failed} falhas`}
                          />
                        )}
                      </div>
                      <span className="text-[9px] text-muted-foreground">
                        {new Date(d.date + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sentiment alert */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Alerta de sentimento
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Button
                variant="outline"
                size="sm"
                className="w-full text-xs mb-3"
                onClick={() => sentimentMut.mutate()}
                disabled={sentimentMut.isPending}
              >
                {sentimentMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                Analisar ultimas 24h
              </Button>

              {sentimentResult && (
                <div className="space-y-2">
                  {sentimentResult.alert ? (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-red-700 font-semibold text-sm">
                        <AlertTriangle className="w-4 h-4" />
                        Alerta ativo
                      </div>
                      <p className="text-xs text-red-600 mt-1">
                        {sentimentResult.percentage}% das mensagens expressam insatisfacao ({sentimentResult.negativeCount}/{sentimentResult.totalAnalyzed})
                      </p>
                    </div>
                  ) : (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm">
                        <CheckCircle2 className="w-4 h-4" />
                        Tudo bem
                      </div>
                      <p className="text-xs text-emerald-600 mt-1">
                        Sentimento dentro do normal ({sentimentResult.negativeCount}/{sentimentResult.totalAnalyzed} negativas)
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Topics analysis */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-blue-500" />
                Topicos mais perguntados
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={() => topicsMut.mutate({})}
                disabled={topicsMut.isPending}
              >
                {topicsMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Brain className="w-3.5 h-3.5 mr-1" />}
                Analisar (7 dias)
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {topicsMut.isPending && (
              <div className="flex items-center gap-2 py-6 justify-center text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-xs">Analisando mensagens...</span>
              </div>
            )}

            {topicsResult && topicsResult.topics?.length > 0 ? (
              <div className="space-y-2">
                <p className="text-[11px] text-muted-foreground">{topicsResult.totalMessages} mensagens analisadas</p>
                {topicsResult.topics.map((t: any, i: number) => (
                  <div key={i} className="flex items-start gap-3 p-2.5 bg-muted/30 rounded-lg border">
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0 mt-0.5">
                      #{i + 1}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">{sanitizeLLMText(t.topic, 100)}</span>
                        <span className="text-[10px] text-muted-foreground">~{typeof t.count === "number" ? t.count : 0} msgs</span>
                      </div>
                      {t.suggestion && (
                        <div className="flex items-start gap-1.5 mt-1">
                          <Lightbulb className="w-3 h-3 text-amber-500 shrink-0 mt-0.5" />
                          <span className="text-xs text-muted-foreground">{sanitizeLLMText(t.suggestion, 300)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : topicsResult && topicsResult.topics?.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">
                {topicsResult.message || "Nenhum topico identificado"}
              </p>
            ) : !topicsMut.isPending ? (
              <p className="text-xs text-muted-foreground py-6 text-center">
                Clique em "Analisar" para identificar os topicos mais frequentes nas conversas
              </p>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
    </PermissionGuard>
  );
}
